/**
 * V5 详细报告 raw → view model。
 *
 * 在粗略 view model（adapters/reportV5Adapter.js）之上叠加 detailed 子块：
 *   detailed.ability_deep_dive  → vm.detailed.abilities (按 5 力顺序)
 *   detailed.growth_path        → vm.detailed.growth_path (phases code → {code,name})
 *
 * 素质（trait_deep_dive / trait_synthesis）已从报告链永久移除（Task 11）。
 *
 * Schema 提议见 docs/archive/v5/AI-HANDOFF.md 2026-05-20 14:00 frontend 条目。
 * 后端最终 schema 可能微调（命名 / 长度），本 adapter 保持 lenient：缺字段不抛异常。
 */
import { toReportV5ViewModel } from './reportV5Adapter';
import {
  ABILITY_LABELS,
  ABILITY_ORDER,
} from '../constants/reportV5Labels';

function abilityName(code) {
  return ABILITY_LABELS[code]?.name || code;
}

export function toReportV5DetailedViewModel(body) {
  // 先拿粗略 VM 作为基底（含 meta / overall / abilities / scenarios / 等）
  const base = toReportV5ViewModel(body);

  // 提取 detailed 块
  const inner = body?.user_report_v5 && typeof body.user_report_v5 === 'object'
    ? body.user_report_v5
    : body;
  const d = inner?.detailed && typeof inner.detailed === 'object' ? inner.detailed : null;

  if (!d) {
    // 未解锁 / 后端没下发 detailed —— 返回基底 VM + 标记
    return { ...base, is_detailed: false, detailed: null };
  }

  // --- ability_deep_dive: 5 力按 ABILITY_ORDER 排，合并粗略 abilities 的分数数据 ---
  const abilityVMByCode = new Map(base.abilities.map((a) => [a.code, a]));
  const deepByCode = d.ability_deep_dive || {};
  const detailedAbilities = ABILITY_ORDER.map((code) => {
    const baseA = abilityVMByCode.get(code) || {};
    const deepA = deepByCode[code] || {};
    return {
      code,
      name: baseA.name || abilityName(code),
      percentage: baseA.percentage ?? 0,
      l_grade: baseA.l_grade || '',
      l_grade_label: baseA.l_grade_label || '',
      current_state_summary: deepA.current_state_summary || '',
      impact_and_direction: deepA.impact_and_direction || '',
    };
  });

  // --- growth_path: phases 里 focus_ability_codes 解析成 {code, name} ---
  const rawGrowth = d.growth_path || {};
  const rawPhases = Array.isArray(rawGrowth.phases) ? rawGrowth.phases : [];
  const phases = rawPhases.slice(0, 3).map((p, idx) => ({
    phase_index: Number(p?.phase_index) || idx + 1,
    phase_label: p?.phase_label || `阶段 ${idx + 1}`,
    phase_title: p?.phase_title || '',
    phase_goal: p?.phase_goal || '',
    learning_items: Array.isArray(p?.learning_items) ? p.learning_items.slice(0, 6) : [],
    focus_abilities: (Array.isArray(p?.focus_ability_codes) ? p.focus_ability_codes : [])
      .slice(0, 3)
      .map((code) => ({ code, name: abilityName(code) })),
  }));

  return {
    ...base,
    is_detailed: true,
    detailed: {
      abilities: detailedAbilities,
      // trait_deep_dive / trait_synthesis intentionally omitted — report chain is traits-free (Task 11)
      // collaboration_style / not_recommended_tasks 已随 Page 4 三列表改版停用（报告链不再消费）
      growth_path: {
        overall_goal: rawGrowth.overall_goal || '',
        phases,
        follow_up_suggestions: Array.isArray(rawGrowth.follow_up_suggestions)
          ? rawGrowth.follow_up_suggestions.slice(0, 5)
          : [],
        application_tips: rawGrowth.application_tips || '',
      },
    },
  };
}
